import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@material-ui/core';
import {
  Schedule,
  VideoCall,
  Phone,
  LocationOn,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  Person,
  Launch,
  Send,
} from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { DateTime } from 'luxon';
import { useUser } from '../auth/useUser';
import { useCampfireQuery } from '../network/useCampfireQuery';
import { useCampfireFetch } from '../network/useCampfireFetch';
import { useApiUrl } from '../config/useApiUrl';
import { useSnackbar } from '../config/useSnackbar';
import { SUYP_GET_EVENT_TASK, SUYP_GET_COMPLETED_TASK_EVENTS } from './model/suyp-event-task-query.gql';
import { SUYPGetEventTask, SUYPGetEventTaskVariables } from './model/__generated__/SUYPGetEventTask';
import {
  SUYPGetCompletedTaskEvents,
  SUYPGetCompletedTaskEventsVariables,
} from './model/__generated__/SUYPGetCompletedTaskEvents';

interface Props {
  taskId: string;
}

interface EventData {
  id: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  startDateTime?: string;
  endDateTime?: string;
  timezone?: string;
  activityLocation?: {
    name: string;
    address: string;
  };
  remoteLocation?: string;
  managers?: Array<{
    managerId: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  }>;
  participantFeedback?: string;
  rescheduleCount: number;
  originalScheduledDate?: string;
  dateCreated: string;
}

export const SUYPEventTask: React.FC<Props> = ({ taskId }) => {
  const { user } = useUser();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const campfireMutation = useCampfireFetch({ defer: true });

  // First query: Get basic task and completed task info
  const { data: taskData, refetch: refetchTaskData } = useCampfireQuery<SUYPGetEventTask, SUYPGetEventTaskVariables>(
    SUYP_GET_EVENT_TASK,
    {
      options: {
        variables: {
          taskId,
          userId: user?.userId || '',
        },
        skip: !taskId || !user?.userId,
      },
    }
  );

  // Get the completed task ID from the first query - now through profile to avoid filters
  const completedTaskForThisTask = taskData?.vm?.profile?.completedTasks?.find((ct: any) => ct.task.taskId === taskId);
  const completedTaskId = completedTaskForThisTask?.completedTaskId;

  // For event tasks, we might not have a completed task yet, but we still want to show the UI
  const isEventTask = taskData?.vm?.task?.isEventTask;

  // Second query: Get events with managers using the working pattern
  // Skip only if we're sure there's no completed task for a non-event task
  const { data: eventsData, refetch: refetchEventsData } = useCampfireQuery<
    SUYPGetCompletedTaskEvents,
    SUYPGetCompletedTaskEventsVariables
  >(SUYP_GET_COMPLETED_TASK_EVENTS, {
    options: {
      variables: { completedTaskId: completedTaskId || '' },
      skip: !completedTaskId, // Skip if no completed task ID
    },
  });

  const refetch = () => {
    if (refetchTaskData) refetchTaskData();
    if (refetchEventsData) refetchEventsData();
  };

  const parseDateTime = (dateTime: string) => {
    // Strip timezone info and parse as local time to prevent unwanted conversion
    const dateTimeWithoutTz = dateTime.replace(/[+-]\d{2}:?\d{0,2}$|Z$/g, '');
    // Use fromISO with the stripped datetime - this will treat it as local time
    return DateTime.fromISO(dateTimeWithoutTz);
  };

  const events: EventData[] = useMemo(() => {
    // Get events from the second query (this is the proper way after fixing the SQL error)
    if (eventsData?.vm?.completedTask?.events) {
      return eventsData.vm.completedTask.events.map((event: any) => ({
        id: event.completedTaskEventId,
        status: event.status as EventData['status'],
        startDateTime: event.startDateTime,
        endDateTime: event.endDateTime,
        timezone: event.startDateTime
          ? (() => {
              const date = parseDateTime(event.startDateTime);
              return date.isValid ? date.offsetNameShort : undefined;
            })()
          : undefined,
        activityLocation: event.activityLocation
          ? {
              name: event.activityLocation.placesAddress.description,
              address: event.activityLocation.placesAddress.formatted,
            }
          : undefined,
        remoteLocation: event.remoteLocation || undefined,
        managers: event.managers || [],
        participantFeedback: event.participantFeedback || undefined,
        rescheduleCount: event.rescheduleCount,
        originalScheduledDate: event.originalScheduledDate,
        dateCreated: event.dateCreated,
      }));
    }

    return [];
  }, [eventsData, taskData, completedTaskId, isEventTask, completedTaskForThisTask]);

  const getLocationInfo = (event: EventData) => {
    if (event.activityLocation) {
      return {
        type: 'location' as const,
        icon: <LocationOn style={{ fontSize: '16px', marginRight: '4px', color: '#757575' }} />,
        text: event.activityLocation.name,
        subtext: event.activityLocation.address,
      };
    }

    if (event.remoteLocation) {
      // Check if it's a URL
      if (event.remoteLocation.match(/^https?:\/\//)) {
        return {
          type: 'video' as const,
          icon: <VideoCall style={{ fontSize: '16px', marginRight: '4px', color: '#757575' }} />,
          text: 'Video Call',
          subtext: event.remoteLocation,
        };
      }
      // Check if it's a phone number
      if (event.remoteLocation.match(/^[+]?[\d\s\-()]+$/)) {
        return {
          type: 'phone' as const,
          icon: <Phone style={{ fontSize: '16px', marginRight: '4px', color: '#757575' }} />,
          text: 'Phone Call',
          subtext: event.remoteLocation,
        };
      }
      // Default to remote location
      return {
        type: 'remote' as const,
        icon: <VideoCall style={{ fontSize: '16px', marginRight: '4px', color: '#757575' }} />,
        text: 'Remote',
        subtext: event.remoteLocation,
      };
    }

    return {
      type: 'tbd' as const,
      icon: <Schedule style={{ fontSize: '16px', marginRight: '4px' }} />,
      text: 'Location TBD',
      subtext: '',
    };
  };

  const getStatusStyle = (status: EventData['status']) => {
    switch (status) {
      case 'completed':
        return {
          backgroundColor: '#e8f5e8',
          color: '#2e7d32',
        };
      case 'scheduled':
        return {
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
        };
      case 'cancelled':
        return {
          backgroundColor: '#ffebee',
          color: '#c62828',
        };
      case 'no_show':
        return {
          backgroundColor: '#fff3e0',
          color: '#ef6c00',
        };
      default:
        return {
          backgroundColor: '#f5f5f5',
          color: '#666',
        };
    }
  };

  const getStatusIcon = (status: EventData['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle style={{ fontSize: '14px', marginRight: '4px', color: '#2e7d32' }} />;
      case 'scheduled':
        return <HourglassEmpty style={{ fontSize: '14px', marginRight: '4px', color: '#1976d2' }} />;
      case 'cancelled':
        return <Cancel style={{ fontSize: '14px', marginRight: '4px', color: '#c62828' }} />;
      case 'no_show':
        return <Cancel style={{ fontSize: '14px', marginRight: '4px', color: '#ef6c00' }} />;
      default:
        return null;
    }
  };

  const formatStatusLabel = (status: EventData['status']) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'scheduled':
        return 'Scheduled';
      case 'cancelled':
        return 'Cancelled';
      case 'no_show':
        return 'No Show';
      default:
        return status;
    }
  };

  const formatDateTime = (startDateTime?: string, endDateTime?: string, timezone?: string) => {
    if (!startDateTime) return 'Date TBD';

    const startDate = parseDateTime(startDateTime);

    if (!startDate.isValid) {
      return 'Invalid date';
    }

    const dateFormatted = startDate.toLocaleString({
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

    const startTimeFormatted = startDate.toLocaleString({
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    let timeRange = startTimeFormatted;

    // Add end time if it exists
    if (endDateTime) {
      const endDate = parseDateTime(endDateTime);
      if (endDate.isValid) {
        const endTimeFormatted = endDate.toLocaleString({
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        timeRange = `${startTimeFormatted} - ${endTimeFormatted}`;
      }
    }

    // Get current timezone offset to compare
    const currentTimezone = DateTime.local().offsetNameShort;
    const shouldShowTimezone = timezone && timezone !== currentTimezone;

    const result = `${dateFormatted}, ${timeRange}`;
    return shouldShowTimezone ? `${result} (${timezone})` : result;
  };

  if (!user) {
    return (
      <Box p={2}>
        <Typography variant='body2' color='textSecondary'>
          Please log in to view event information.
        </Typography>
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box display='flex' justifyContent='center' flex='1 1 auto'>
        <Box maxWidth={720} width={1}>
          <Box
            marginTop={2}
            marginBottom={3}
            paddingTop={3}
            paddingBottom={3}
            paddingLeft={4}
            paddingRight={2}
            border={'1px solid #e9e9e9'}
            bgcolor='#ffffff'
            borderRadius={7}
          >
            <Typography variant='body2' color='textSecondary' style={{ textAlign: 'center' }}>
              {!completedTaskId && isEventTask
                ? 'Events will appear here once scheduled.'
                : 'No events have been scheduled yet.'}
            </Typography>
            {!completedTaskId && isEventTask && (
              <Typography
                variant='caption'
                color='textSecondary'
                display='block'
                style={{ textAlign: 'center', marginTop: '8px' }}
              >
                An event will be scheduled for this task and you&apos;ll be notified.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  const calculateDuration = (start?: string, end?: string) => {
    if (!start || !end) return '';

    const startDate = parseDateTime(start);
    const endDate = parseDateTime(end);

    if (!startDate.isValid || !endDate.isValid) {
      return 'Invalid duration';
    }

    const durationMs = endDate.toMillis() - startDate.toMillis();

    if (durationMs < 0) {
      return 'Invalid duration';
    }

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const handleContactManagers = (event: EventData) => {
    setSelectedEvent(event);
    setShowContactDialog(true);
  };

  const handleSubmitContactForm = async (values: { message: string }) => {
    if (!selectedEvent) return;

    try {
      const response = await campfireMutation.run({
        url: `${apiUrl}/vm/task/event/contact-managers`,
        method: 'post',
        data: {
          eventId: selectedEvent.id,
          message: values.message,
        },
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Message sent successfully to event organisers',
          variant: 'success',
        });
        setShowContactDialog(false);
        setSelectedEvent(null);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send message. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleAddFeedback = (event: EventData) => {
    setSelectedEvent(event);
    setShowFeedbackDialog(true);
  };

  const handleSubmitFeedback = async (values: { participantFeedback: string }) => {
    if (!selectedEvent) return;

    try {
      const response = await campfireMutation.run({
        url: `${apiUrl}/vm/task/event/complete`,
        method: 'post',
        data: {
          eventId: selectedEvent.id,
          participantFeedback: values.participantFeedback,
        },
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Feedback added successfully',
          variant: 'success',
        });
        setShowFeedbackDialog(false);
        setSelectedEvent(null);
        // Refetch data to show the updated feedback
        refetch();
      } else {
        throw new Error('Failed to add feedback');
      }
    } catch (error) {
      console.error('Error adding feedback:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add feedback. Please try again.',
        variant: 'error',
      });
    }
  };

  return (
    <Box>
      <Box display='flex' justifyContent='center' flex='1 1 auto'>
        <Box maxWidth={720} width={1}>
          <Box
            marginTop={2}
            marginBottom={3}
            paddingTop={3}
            paddingBottom={3}
            paddingLeft={4}
            paddingRight={2}
            border={'1px solid #e9e9e9'}
            bgcolor='#ffffff'
            borderRadius={7}
          >
            {events.map((event, index) => {
              const locationInfo = getLocationInfo(event);
              const statusStyle = getStatusStyle(event.status);

              return (
                <Box key={event.id}>
                  {index > 0 && <Divider style={{ margin: '20px 0' }} />}

                  {/* Event Header with Status */}
                  <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
                    <Typography variant='h6' style={{ fontWeight: 'bold' }}>
                      Event {events.length > 1 ? `${index + 1}` : ''}
                    </Typography>
                    <Chip
                      icon={getStatusIcon(event.status) || undefined}
                      label={formatStatusLabel(event.status)}
                      style={{
                        ...statusStyle,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                      size='small'
                    />
                  </Box>

                  {/* Event Details */}
                  <Box>
                    {/* Date and Time with larger icon */}
                    <Box display='flex' alignItems='center' mb={2}>
                      <Schedule style={{ fontSize: '20px', marginRight: '12px', color: '#1976d2' }} />
                      <Box>
                        <Typography variant='body1' style={{ fontWeight: 600, color: '#333' }}>
                          {formatDateTime(event.startDateTime, event.endDateTime, event.timezone)}
                        </Typography>
                        {event.endDateTime && (
                          <Typography variant='caption' color='textSecondary'>
                            Duration: {calculateDuration(event.startDateTime, event.endDateTime)}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Location/Call Information with improved styling */}
                    <Box display='flex' alignItems='flex-start' mb={2}>
                      {React.cloneElement(locationInfo.icon as React.ReactElement, {
                        style: { fontSize: '20px', marginRight: '12px', color: '#757575' },
                      })}
                      <Box flex={1}>
                        <Typography variant='body2' style={{ fontWeight: 600, marginBottom: '4px' }}>
                          {locationInfo.text}
                        </Typography>
                        {locationInfo.subtext && (
                          <Typography variant='body2' color='textSecondary'>
                            {locationInfo.subtext}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Managers with improved layout */}
                    {event.managers && event.managers.length > 0 && (
                      <Box display='flex' alignItems='flex-start' mb={2}>
                        <Person style={{ fontSize: '20px', marginRight: '12px', color: '#757575' }} />
                        <Box>
                          <Typography variant='body2' style={{ fontWeight: 600, marginBottom: '4px' }}>
                            Meeting with:
                          </Typography>
                          <Typography variant='body2' color='textSecondary'>
                            {event.managers
                              .map((manager) => `${manager.profile.firstName} ${manager.profile.lastName}`)
                              .join(', ')}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Reschedule Information */}
                    {event.rescheduleCount > 0 && (
                      <Box mb={2}>
                        <Typography variant='caption' color='textSecondary'>
                          Rescheduled {event.rescheduleCount} time{event.rescheduleCount > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    )}

                    {/* Participant Feedback */}
                    {event.participantFeedback && event.status === 'completed' && (
                      <Box mt={2} p={1.5} bgcolor='#f5f5f5' borderRadius={1}>
                        <Typography
                          variant='caption'
                          color='textSecondary'
                          display='block'
                          style={{ marginBottom: '4px', fontWeight: 600 }}
                        >
                          Your Feedback:
                        </Typography>
                        <Typography variant='body2'>{event.participantFeedback}</Typography>
                      </Box>
                    )}

                    {/* Add Feedback Button for Completed Events without Feedback */}
                    {event.status === 'completed' && !event.participantFeedback && (
                      <Box mt={2}>
                        <TabletButton
                          size='small'
                          variant='outlined'
                          color='primary'
                          onClick={() => handleAddFeedback(event)}
                          startIcon={<CheckCircle />}
                        >
                          Add Feedback
                        </TabletButton>
                      </Box>
                    )}

                    {/* Enhanced Call/Meeting Buttons */}
                    {event.status === 'scheduled' &&
                      (locationInfo.type === 'video' || locationInfo.type === 'phone') &&
                      locationInfo.subtext && (
                        <Box mt={2} mb={2}>
                          {locationInfo.type === 'phone' ? (
                            <Button
                              variant='contained'
                              onClick={() => window.open(`tel:${locationInfo.subtext}`, '_self')}
                              style={{
                                backgroundColor: '#4caf50',
                                color: 'white',
                                width: '100%',
                              }}
                              startIcon={<Phone style={{ fontSize: '16px' }} />}
                            >
                              Call {locationInfo.subtext}
                            </Button>
                          ) : (
                            <Button
                              variant='contained'
                              onClick={() => window.open(locationInfo.subtext, '_blank')}
                              style={{
                                backgroundColor: locationInfo.subtext.includes('zoom.us')
                                  ? '#2D8CFF'
                                  : locationInfo.subtext.includes('meet.google.com')
                                  ? '#34A853'
                                  : locationInfo.subtext.includes('teams.microsoft.com')
                                  ? '#464EB8'
                                  : '#1976d2',
                                color: 'white',
                                width: '100%',
                              }}
                              startIcon={<VideoCall style={{ fontSize: '16px' }} />}
                              endIcon={<Launch style={{ fontSize: '14px' }} />}
                            >
                              {locationInfo.subtext.includes('zoom.us')
                                ? 'Join Zoom Meeting'
                                : locationInfo.subtext.includes('meet.google.com')
                                ? 'Join Google Meet'
                                : locationInfo.subtext.includes('teams.microsoft.com')
                                ? 'Join Microsoft Teams'
                                : 'Join Video Call'}
                            </Button>
                          )}
                        </Box>
                      )}

                    {/* Contact Managers Button */}
                    {event.managers && event.managers.length > 0 && (
                      <Box mt={2}>
                        <Button
                          variant='contained'
                          onClick={() => handleContactManagers(event)}
                          style={{
                            width: '100%',
                            backgroundColor: '#1976d2',
                            color: '#ffffff',
                            borderRadius: '8px',
                            padding: '10px 16px',
                            textTransform: 'none',
                            fontWeight: 500,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                          startIcon={<Send style={{ fontSize: '18px' }} />}
                        >
                          Contact Event Organisers
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onClose={() => setShowFeedbackDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Add Feedback</DialogTitle>
        <Formik
          initialValues={{ participantFeedback: '' }}
          validationSchema={Yup.object({
            participantFeedback: Yup.string().required('Feedback is required'),
          })}
          onSubmit={handleSubmitFeedback}
        >
          {({ values, handleChange, handleBlur, errors, touched }) => (
            <Form>
              <DialogContent>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  name='participantFeedback'
                  label='Your Feedback'
                  placeholder='Share your experience from this event...'
                  value={values.participantFeedback}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.participantFeedback && touched.participantFeedback)}
                  helperText={
                    errors.participantFeedback && touched.participantFeedback
                      ? errors.participantFeedback
                      : 'This feedback will be visible to the organisation'
                  }
                  variant='outlined'
                />
              </DialogContent>
              <DialogActions style={{ padding: '16px 24px', gap: '12px' }}>
                <TabletButton onClick={() => setShowFeedbackDialog(false)} variant='outlined' color='primary'>
                  Cancel
                </TabletButton>
                <TabletButton type='submit' variant='contained' color='primary' disabled={campfireMutation.isLoading}>
                  {campfireMutation.isLoading ? 'Submitting...' : 'Submit Feedback'}
                </TabletButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Contact Managers Dialog */}
      <Dialog open={showContactDialog} onClose={() => setShowContactDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle>
          Contact Event Organisers
          {selectedEvent?.managers && selectedEvent.managers.length > 0 && (
            <Typography variant='subtitle2' color='textSecondary' style={{ marginTop: '4px' }}>
              {selectedEvent.managers
                .map((manager) => `${manager.profile.firstName} ${manager.profile.lastName}`)
                .join(', ')}
            </Typography>
          )}
        </DialogTitle>
        <Formik
          initialValues={{ message: '' }}
          validationSchema={Yup.object({
            message: Yup.string()
              .required('Message is required')
              .max(1000, 'Message must be 1000 characters or less'),
          })}
          onSubmit={handleSubmitContactForm}
        >
          {({ values, handleChange, handleBlur, errors, touched }) => (
            <Form>
              <DialogContent style={{ paddingTop: '8px' }}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  name='message'
                  label='Message'
                  placeholder='Type your message to the event organisers...'
                  value={values.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!(errors.message && touched.message)}
                  helperText={
                    errors.message && touched.message
                      ? errors.message
                      : 'Your message will be sent to the event organisers'
                  }
                  variant='outlined'
                />
              </DialogContent>
              <DialogActions style={{ padding: '16px 24px', gap: '12px' }}>
                <TabletButton onClick={() => setShowContactDialog(false)} variant='outlined' color='primary'>
                  Cancel
                </TabletButton>
                <TabletButton type='submit' variant='contained' color='primary' disabled={campfireMutation.isLoading}>
                  {campfireMutation.isLoading ? 'Sending...' : 'Send Message'}
                </TabletButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};
